/**
 * Global fetch interceptor.
 * When the backend API is unreachable, the static host returns HTML (SPA index/404).
 * This interceptor reads every response body and swaps HTML for a synthetic JSON error
 * BEFORE any library tries to parse it.
 */

let backendDown = false;
let backendDownTimestamp = 0;
const BACKEND_DOWN_COOLDOWN_MS = 5000; // 5 seconds — shorter for faster retry

const _origFetch = globalThis.fetch;

/** Bypass the interceptor for diagnostic checks */
export function getOriginalFetch(): typeof fetch {
  return _origFetch;
}

function isBackendDown(): boolean {
  if (!backendDown) return false;
  if (Date.now() - backendDownTimestamp > BACKEND_DOWN_COOLDOWN_MS) {
    backendDown = false;
    return false;
  }
  return true;
}

globalThis.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  const isApiCall = url.includes("/api/") || url.includes("api/");

  // If we already know backend is unreachable, skip the network call (with cooldown)
  if (isApiCall && isBackendDown()) {
    return makeSyntheticResponse("Backend unreachable — running in offline mode");
  }

  let response: Response;
  try {
    response = await _origFetch.call(globalThis, input, init);
  } catch (err) {
    if (isApiCall) {
      backendDown = true;
      backendDownTimestamp = Date.now();
    }
    const msg = err instanceof Error ? err.message : "Network error";
    return makeSyntheticResponse(msg);
  }

  // Only intercept API calls
  if (!isApiCall) {
    return response;
  }

  // Clone and peek at body text
  const peek = response.clone();
  let text: string;
  try {
    text = await peek.text();
  } catch {
    return response;
  }

  // Log the first 200 chars of API responses for debugging (in production too, this is critical)
  if (text.trimStart().startsWith("<")) {
    console.warn("[fetchInterceptor] API returned HTML:", url, "status:", response.status, "body preview:", text.substring(0, 200));

    // ONLY treat as "backend down" if the server returned 200 OK with HTML.
    // A 200 HTML response means the SPA rewrite kicked in (the API file was not reached).
    // A 4xx/5xx HTML response is a real server error — pass it through so the caller sees it.
    if (response.status === 200) {
      backendDown = true;
      backendDownTimestamp = Date.now();
      return makeSyntheticResponse(
        `API request was rewritten to HTML. Ensure PHP is enabled and .htaccess is processed. URL: ${url}`
      );
    }
    // For 404/500 HTML: pass through — the server is running but the API file is missing or errored
  }

  // Reset backendDown on successful API response
  backendDown = false;
  return response;
};

function makeSyntheticResponse(message: string): Response {
  const body = JSON.stringify({
    error: message,
    suggestion: "Check browser DevTools Network tab for the exact request/response. Common causes: (1) PHP not enabled on server, (2) .htaccess ignored (nginx or AllowOverride None), (3) Database connection failed.",
  });
  return new Response(body, {
    status: 503,
    headers: { "content-type": "application/json" },
  });
}
